import { Vue, Component, Prop } from 'vue-property-decorator'
import { ConfigurationDeviceResponse } from '@/modules/configurationModule/detail/instrumentation/devices/services/configuration-device/configuration-device.dto'
import {
    ConfigurationInstrumentationDevicesModalVisibilityFormSectionAssayState,
    initialConfigurationInstrumentationDevicesModalVisibilityFormSectionAssayState
} from '@/modules/configurationModule/detail/instrumentation/devices/components/modal-visibility-form-section-assay/configuration-instrumentation-devices-modal-visibility-form-section-assay.model'
import { CommonFilterModel, CommonParams } from '@/api/resources/common/shared/models/common.models'
import { PAGINATOR_BIG_PAGE_SIZE } from '@/shared/services/services-constants'
import {
    ConfigurationDeviceSectionAssayCreateRequest,
    ConfigurationDeviceSectionAssayResponse
} from '@/modules/configurationModule/detail/instrumentation/devices/services/configuration-device-section-assay/configuration-device-section-assay.dto'
import { configurationDeviceSectionAssayService } from '@/modules/configurationModule/detail/instrumentation/devices/services/configuration-device-section-assay/configuration-device-section-assay.service'
import { AssayModalAssayWithValuableConceptResponse } from '@/components/assays-view/services/assays-view-assay/assays-view-assay.dto'
import { assaysViewAssayService } from '@/components/assays-view/services/assays-view-assay/assays-view-assay.service'
import { ConfigurationSectionResponse } from '@/modules/configurationModule/detail/local-laboratory-module/sections/services/configuration-section/configuration-section.dto'
import { configurationSectionService } from '@/modules/configurationModule/detail/local-laboratory-module/sections/services/configuration-section/configuration-section.service'
import { GridUtils } from '@/shared/util/grid-utils'

@Component
export default class ConfigurationInstrumentationDevicesModalVisibilityFormSectionAssay extends Vue {
    @Prop() readonly device!: ConfigurationDeviceResponse

    state: ConfigurationInstrumentationDevicesModalVisibilityFormSectionAssayState

    get canAdd(): boolean {
        return !!this.state.section?.id || !!this.state.assay?.id
    }

    constructor() {
        super()
        this.state = initialConfigurationInstrumentationDevicesModalVisibilityFormSectionAssayState()
    }

    /// SERVICES CALLS ///

    private async _getSections(): Promise<ConfigurationSectionResponse[]> {
        const params: CommonParams = {
            size: PAGINATOR_BIG_PAGE_SIZE
        }
        const response = await this.$_toCallSecureLoading(configurationSectionService.paginator(undefined, params, this.state.laboratoryId))
        return response?.content ?? []
    }

    private async _getAssays(): Promise<AssayModalAssayWithValuableConceptResponse[]> {
        const response = await this.$_toCallSecureLoading(assaysViewAssayService.getCompletedAssays(this.state.laboratoryId))
        return response ?? []
    }

    private async _getDeviceSectionsAssays(): Promise<ConfigurationDeviceSectionAssayResponse[]> {
        const filterModel: CommonFilterModel = {
            'device.id': GridUtils.getNumberFilterModel('equals', this.device.id)
        }
        const params: CommonParams = {
            size: PAGINATOR_BIG_PAGE_SIZE
        }
        const response = await this.$_toCallUnique(configurationDeviceSectionAssayService.paginator(filterModel, params, this.state.laboratoryId))
        return response?.content ?? []
    }

    private async _createDeviceSectionAssay(createRequest: ConfigurationDeviceSectionAssayCreateRequest): Promise<ConfigurationDeviceSectionAssayResponse | null> {
        const response = await this.$_toCallUnique(configurationDeviceSectionAssayService.create(this.state.laboratoryId, createRequest))
        if (response) {
            this.$_showToastSuccess(this.$tc('configuration.toastMessages.created'))
            return response
        }
        return null
    }

    private async _deleteDeviceSectionAssay(deviceSectionAssayId: number): Promise<void | null> {
        const response = await this.$_toCallUnique(configurationDeviceSectionAssayService.delete(this.state.laboratoryId, deviceSectionAssayId))
        if (response !== null) {
            this.$_showToastSuccess(this.$tc('configuration.toastMessages.deleted'))
            return response
        }
        return null
    }

    ///

    private async _loadSections(): Promise<void> {
        const sections = await this._getSections()
        this.state.sections = sections.filter(section => section.active)
    }

    private async _loadAssays(): Promise<void> {
        const assays = await this._getAssays()
        this.state.assays = assays.filter(assay => assay.active)
    }

    private async _loadDeviceSectionsAssays(): Promise<void> {
        this.state.deviceSectionsAssays = await this._getDeviceSectionsAssays()
    }

    /// HOOKS ///

    async mounted() {
        try {
            this.$_updateLoading(true)

            await Promise.all([
                this._loadAssays(),
                this._loadSections(),
                this._loadDeviceSectionsAssays()
            ])
        } finally {
            this.$_updateLoading(false)
        }
    }

    ///

    getSectionLabelFromDeviceSectionAssay(deviceSectionAssay: ConfigurationDeviceSectionAssayResponse): string {
        return deviceSectionAssay.section?.name ?? ''
    }

    getAssayLabelFromDeviceSectionAssay(deviceSectionAssay: ConfigurationDeviceSectionAssayResponse): string {
        return deviceSectionAssay.assay?.name ?? ''
    }

    /// OPERATIONS ///

    async addOperation(): Promise<void> {
        const createRequest: ConfigurationDeviceSectionAssayCreateRequest = {
            deviceId: this.device.id,
            sectionId: this.state.section?.id,
            assayId: this.state.assay?.id
        }
        if (await this._createDeviceSectionAssay(createRequest)) {
            await this._loadDeviceSectionsAssays()
        }
    }

    async deleteOperation(deviceSectionAssayId: number): Promise<void> {
        if (await this._deleteDeviceSectionAssay(deviceSectionAssayId) !== null) {
            await this._loadDeviceSectionsAssays()
        }
    }
}
